import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, Btn, Badge, useSave, AInput } from "../AdminUI";
import { AlertTriangle, Star } from "lucide-react";

const Toggle = ({ label, checked, onChange, hint, disabled, testid }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
    <div><p className="font-semibold text-sm text-[var(--navy)]">{label}</p>{hint && <p className="text-xs text-gray-400">{hint}</p>}</div>
    <button onClick={() => !disabled && onChange(!checked)} data-testid={testid} disabled={disabled}
      className={`shrink-0 w-11 h-6 rounded-full transition-colors ${checked ? "bg-green-500" : "bg-gray-300"} ${disabled ? "opacity-50" : ""} relative`}>
      <span className={`absolute top-0.5 h-5 w-5 bg-white rounded-full transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  </div>
);

const ReviewSettingsCard = () => {
  const [r, setR] = useState(null);
  const [saving, save] = useSave();
  useEffect(() => { api.get("/admin/review-settings").then((res) => setR(res.data)).catch(() => {}); }, []);
  if (!r) return null;
  const upd = (k, v) => setR({ ...r, [k]: v });
  const submit = () => save(async () => { const res = await api.patch("/admin/review-settings", r); setR(res.data); });
  return (
    <Card title="Customer Reviews">
      <div className="flex items-center gap-2 mb-3"><Star className="w-4 h-4 text-[#F6B21B]" /><Badge color="green">In-House</Badge><span className="text-xs text-gray-400">No Google API — native review system</span></div>
      <Toggle label="Reviews Enabled" checked={r.reviews_enabled} onChange={(v) => upd("reviews_enabled", v)} hint="Show reviews on the public site" testid="reviews-enabled" />
      <Toggle label="Public Submission Enabled" checked={r.public_submission_enabled} onChange={(v) => upd("public_submission_enabled", v)} hint="Allow visitors to leave reviews" testid="reviews-public-submit" />
      <Toggle label="Require Admin Approval" checked={r.require_approval} onChange={(v) => upd("require_approval", v)} hint="Public reviews stay pending until approved" testid="reviews-require-approval" />
      <div className="mt-3 grid grid-cols-1 gap-3">
        <AInput label="Google Business Profile URL" value={r.google_profile_url || ""} onChange={(e) => upd("google_profile_url", e.target.value)} placeholder="Paste from your Google Business Profile" testid="google-profile-url" />
        <AInput label="Google Leave-a-Review URL" value={r.google_leave_url || ""} onChange={(e) => upd("google_leave_url", e.target.value)} placeholder="Your Google review short link" testid="google-leave-url" />
        <div><label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Default Review Request Message</label>
          <textarea rows={3} className="w-full px-3 rounded-md border border-gray-300 py-2 text-sm" value={r.default_request_message || ""} onChange={(e) => upd("default_request_message", e.target.value)} data-testid="default-request-message" />
          <p className="text-xs text-gray-400 mt-1">Use {"{link}"} where the secure review link should appear.</p></div>
      </div>
      <div className="mt-3"><Btn onClick={submit} loading={saving} data-testid="review-settings-save">Save Review Settings</Btn></div>
    </Card>
  );
};

export default function Integrations() {
  const [s, setS] = useState(null);
  const [saving, save] = useSave();
  useEffect(() => { api.get("/admin/integrations").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;

  const upd = (k, v) => setS({ ...s, [k]: v });
  const submit = () => save(() => api.patch("/admin/integrations", s));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-head font-extrabold text-3xl text-[var(--navy)] uppercase tracking-tight">Integrations</h1>
        <Btn onClick={submit} loading={saving} data-testid="integrations-save">Save Settings</Btn>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">All communication providers are inactive by default with kill switches engaged. No live emails, texts, or automated calls are sent until you connect an approved provider and disable the relevant kill switch.</p>
      </div>

      <Card title="Email">
        <Toggle label="Email Enabled" checked={s.email_enabled} onChange={(v) => upd("email_enabled", v)} hint="Master switch for outbound email" testid="email-enabled" />
        <div className="mt-3"><AInput label="Email Provider" value={s.email_provider || ""} onChange={(e) => upd("email_provider", e.target.value)} placeholder="Not connected" /></div>
      </Card>

      <Card title="SMS & Missed-Call Text-Back">
        <Toggle label="SMS Enabled" checked={s.sms_enabled} onChange={(v) => upd("sms_enabled", v)} testid="sms-enabled" />
        <Toggle label="SMS Kill Switch (block all sending)" checked={s.sms_kill_switch} onChange={(v) => upd("sms_kill_switch", v)} testid="sms-kill" />
        <Toggle label="Missed-Call Text-Back" checked={s.missed_call_textback_enabled} onChange={(v) => upd("missed_call_textback_enabled", v)} hint="Auto-reply to missed calls" testid="missed-call" />
        <div className="mt-3"><AInput label="SMS Provider" value={s.sms_provider || ""} onChange={(e) => upd("sms_provider", e.target.value)} placeholder="Not connected" /></div>
      </Card>

      <Card title="AI Phone Receptionist">
        <Toggle label="AI Phone Enabled" checked={s.ai_phone_enabled} onChange={(v) => upd("ai_phone_enabled", v)} hint="Future feature — inactive" testid="aiphone-enabled" />
        <div className="mt-3"><AInput label="AI Phone Provider" value={s.ai_phone_provider || ""} onChange={(e) => upd("ai_phone_provider", e.target.value)} placeholder="Provider-neutral — not connected" /></div>
        <p className="text-xs text-gray-400 mt-2">The system is prepared for a future AI phone receptionist. No provider is hardcoded or active.</p>
      </Card>

      <Card title="Client Financing (EasyQuickFund)">
        <Toggle label="Financing Enabled" checked={s.financing_enabled} onChange={(v) => upd("financing_enabled", v)} testid="financing-enabled" />
        <Toggle label="Financing Kill Switch (block live submissions)" checked={s.financing_kill_switch} onChange={(v) => upd("financing_kill_switch", v)} testid="financing-kill" />
        <div className="mt-3"><AInput label="Financing Provider" value={s.financing_provider || ""} onChange={(e) => upd("financing_provider", e.target.value)} /></div>
      </Card>

      <Card title="Meta Ads Publishing">
        <Toggle label="Meta Ads Publishing" checked={s.meta_ads_enabled} onChange={(v) => upd("meta_ads_enabled", v)} hint="Ad Maker never auto-publishes. Inactive." testid="meta-enabled" />
      </Card>

      <ReviewSettingsCard />

      <div className="flex justify-end"><Btn onClick={submit} loading={saving}>Save Settings</Btn></div>
    </div>
  );
}
